import { CommonModule } from '@angular/common';
import { ChangeDetectorRef, Component, Input, Output, EventEmitter, OnChanges, SimpleChanges } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ApiService } from '../../services/api.service';
import { Person } from '../../models/campaign.model';
import { DomSanitizer } from '@angular/platform-browser';

/** Reusable approver picker with Graph search, photos, and validation. */
@Component({
  selector: 'app-approver-picker',
  standalone: true,
  imports: [CommonModule, FormsModule],
  templateUrl: './approver-picker.component.html',
  styleUrls: ['./approver-picker.component.css'],
})
export class ApproverPickerComponent implements OnChanges {
  @Input() approverEmail: string = '';
  @Output() approverEmailChange = new EventEmitter<string>();

  query = '';
  candidates: Person[] = [];
  selectedApprover: Person | null = null;
  photoDataUrls = new Map<string, string>();  // Cache for photo data URLs
  private readonly noPhotoEmails = new Set<string>();  // Emails known to have no photo (avoid re-fetching)
  shown = 10;
  private readonly pageSize = 10;
  searching = false;
  showDropdown = false;

  constructor(
    private api: ApiService,
    private cdr: ChangeDetectorRef,
    private sanitizer: DomSanitizer
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    // If approverEmail changes from the parent (e.g. chat/generate set a new
    // group-verified approver, or the form was reset), keep the picker in
    // sync — even if an approver was already locked in previously.
    if (!changes['approverEmail']) {
      return;
    }
    const email = (this.approverEmail || '').trim();
    const currentlyLocked = this.selectedApprover?.email || '';
    if (email && email.toLowerCase() !== currentlyLocked.toLowerCase()) {
      this.lockApproverByEmail(email);
    } else if (!email && this.selectedApprover) {
      this.selectedApprover = null;
      this.query = '';
    }
  }

  /** Show a minimal placeholder immediately, then upgrade to the full Graph
   * profile (display name + photo) once resolved, so an approver set
   * externally (chat, generated draft) renders "locked" just like a manual
   * selection. */
  private lockApproverByEmail(email: string): void {
    this.selectedApprover = { email, display_name: email };
    this.api.searchApprovers(email, 5).subscribe({
      next: (people) => {
        const match = people.find((p) => p.email.toLowerCase() === email.toLowerCase());
        if (match) {
          this.selectedApprover = match;
          if (match.photo_url && !this.photoDataUrls.has(match.email) && !this.noPhotoEmails.has(match.email)) {
            this.fetchPhotoAsDataUrl(match);
          }
        }
        this.cdr.detectChanges();
      },
      error: () => {
        // Keep the minimal placeholder; the email is still locked in.
        this.cdr.detectChanges();
      },
    });
  }

  search(): void {
    const trimmed = this.query.trim();
    if (!trimmed) {
      this.candidates = [];
      this.showDropdown = false;
      return;
    }
    this.searching = true;
    this.showDropdown = true;
    this.api.searchApprovers(trimmed, 25).subscribe({
      next: (people) => {
        this.candidates = people;
        // Fetch photos for all candidates (skip ones already known to have none)
        this.candidates.forEach(person => {
          if (person.photo_url && !this.photoDataUrls.has(person.email) && !this.noPhotoEmails.has(person.email)) {
            this.fetchPhotoAsDataUrl(person);
          }
        });
        this.shown = this.pageSize;
        this.searching = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.candidates = [];
        this.searching = false;
        this.cdr.detectChanges();
      },
    });
  }

  private fetchPhotoAsDataUrl(person: Person): void {
    if (!person.photo_url) return;

    // Fetch the photo blob using the API service and convert to data URL
    this.api.fetchPhotoBlob(person.photo_url).subscribe({
      next: (blob) => {
        const reader = new FileReader();
        reader.onloadend = () => {
          const dataUrl = reader.result as string;
          this.photoDataUrls.set(person.email, dataUrl);
          this.cdr.detectChanges();
        };
        reader.readAsDataURL(blob);
      },
      error: () => {
        // No photo available (404) or fetch failed — remember this so we don't
        // keep re-requesting the same user's photo on every re-render/search.
        this.noPhotoEmails.add(person.email);
        this.cdr.detectChanges();
      },
    });
  }

  get visibleCandidates(): Person[] {
    return this.candidates.slice(0, this.shown);
  }

  showMore(): void {
    this.shown = Math.min(this.shown + this.pageSize, this.candidates.length);
  }

  get remainingCount(): number {
    return Math.min(this.pageSize, this.candidates.length - this.visibleCandidates.length);
  }

  selectApprover(person: Person): void {
    this.selectedApprover = person;
    this.approverEmail = person.email;
    this.approverEmailChange.emit(person.email);
    // Fetch photo for selected approver if not already cached and not already
    // known to have no photo.
    if (person.photo_url && !this.photoDataUrls.has(person.email) && !this.noPhotoEmails.has(person.email)) {
      this.fetchPhotoAsDataUrl(person);
    }
    this.query = '';
    this.candidates = [];
    this.showDropdown = false;
    this.cdr.detectChanges();
  }

  clearApprover(): void {
    this.selectedApprover = null;
    this.approverEmail = '';
    this.approverEmailChange.emit('');
    this.query = '';
    this.candidates = [];
    this.showDropdown = false;
    this.cdr.detectChanges();
  }

  hasPhoto(person: Person): boolean {
    // Return true if we have a photo URL and have successfully cached it as a data URL
    return !!person.photo_url && !!this.photoDataUrls.get(person.email);
  }

  getPhotoUrl(person: Person): string {
    // Return the cached data URL if available
    return this.photoDataUrls.get(person.email) || '';
  }

  getInitial(person: Person): string {
    const name = person.display_name || person.email || '';
    return name.charAt(0).toUpperCase();
  }

  onInputChange(value: string): void {
    this.query = value;
    if (value.trim()) {
      this.search();
    } else {
      this.candidates = [];
      this.showDropdown = false;
    }
  }

  closeDropdown(): void {
    setTimeout(() => {
      this.showDropdown = false;
    }, 100);
  }
}
